import api from "./api";

const refundService = {
  // Create manual refund
  createManualRefund: async (refundData) => {
    try {
      // Ensure we have a valid token before making the request
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found. Please login again.");
      }

      const response = await api.post("/api/refunds/manual", refundData);
      return response.data;
    } catch (error) {
      console.error("Error creating manual refund:", error);

      // Re-throw the error so the component can handle it
      // The interceptor should handle token refresh automatically
      throw error;
    }
  },

  // Create automatic refund (via Razorpay)
  createAutomaticRefund: async (refundData) => {
    try {
      // Ensure we have a valid token before making the request
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found. Please login again.");
      }

      const response = await api.post("/api/refunds/automatic", refundData);
      return response.data;
    } catch (error) {
      console.error("Error creating automatic refund:", error);

      // Re-throw the error so the component can handle it
      // The interceptor should handle token refresh automatically
      throw error;
    }
  },

  // Get refund by ID
  getRefundById: async (refundId) => {
    try {
      const response = await api.get(`/api/refunds/${refundId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching refund:", error);
      throw error;
    }
  },

  // Get refunds by order ID
  getRefundsByOrder: async (orderId) => {
    try {
      const response = await api.get(`/api/refunds/order/${orderId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching refunds by order:", error);
      throw error;
    }
  },

  // Get refunds by payment ID
  getRefundsByPayment: async (paymentId) => {
    try {
      const response = await api.get(`/api/refunds/payment/${paymentId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching refunds by payment:", error);
      throw error;
    }
  },

  // Get refunds by customer ID
  getRefundsByCustomer: async (customerId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.refund_status)
        params.append("refund_status", filters.refund_status);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.offset) params.append("offset", filters.offset);

      const response = await api.get(
        `/api/refunds/customer/${customerId}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching refunds by customer:", error);
      throw error;
    }
  },

  // Get all refunds with filters
  getAllRefunds: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.refund_status)
        params.append("refund_status", filters.refund_status);
      if (filters.refund_type)
        params.append("refund_type", filters.refund_type);
      if (filters.refund_mode)
        params.append("refund_mode", filters.refund_mode);
      if (filters.order_id) params.append("order_id", filters.order_id);
      if (filters.payment_id) params.append("payment_id", filters.payment_id);
      if (filters.customer_id)
        params.append("customer_id", filters.customer_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.offset) params.append("offset", filters.offset);

      const response = await api.get(`/api/refunds/all?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching all refunds:", error);
      throw error;
    }
  },

  // Get refund summary
  getRefundSummary: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.customer_id)
        params.append("customer_id", filters.customer_id);
      if (filters.order_id) params.append("order_id", filters.order_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);

      const response = await api.get(
        `/api/refunds/summary?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching refund summary:", error);
      throw error;
    }
  },

  // Update refund status
  updateRefundStatus: async (refundId, status, notes) => {
    try {
      const response = await api.patch(`/api/refunds/${refundId}/status`, {
        refund_status: status,
        notes: notes,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating refund status:", error);
      throw error;
    }
  },
};

export default refundService;
